"use client";

import { useContext, useEffect, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useSelectedLayoutSegment } from "next/navigation";
import { fontLogo } from "@/assets/fonts";
import LogoBlack from "@/assets/logo/logo-black.svg";
import LogoWhite from "@/assets/logo/logo-white.svg";
import { ChevronDown } from "lucide-react";
import { useSession } from "next-auth/react";
import { useTheme } from "next-themes";
import { docsConfig } from "@/config/docs";
import { marketingConfig } from "@/config/marketing";
import { siteConfig } from "@/config/site";
import { cn } from "@/lib/utils";
import { useScroll } from "@/hooks/use-scroll";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { DocsSearch } from "@/components/docs/search";
import { ModalContext } from "@/components/modals/providers";
import { Icons } from "@/components/shared/icons";
import MaxWidthWrapper from "@/components/shared/max-width-wrapper";
import { ModeToggle } from "./mode-toggle";

interface NavBarProps {
  scroll?: boolean;
  large?: boolean;
}

export function NavBar({ scroll = false }: NavBarProps) {
  const scrolled = useScroll(50);
  const { data: session, status } = useSession();
  const { setShowSignInModal } = useContext(ModalContext);

  const selectedLayout = useSelectedLayoutSegment();
  const documentation = selectedLayout === "docs";
  const pathname = usePathname();

  const { setTheme, resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const configMap = {
    docs: docsConfig.mainNav,
  };

  const links =
    (selectedLayout && configMap[selectedLayout]) || marketingConfig.mainNav;

  return (
    <header
      className={cn(
        `sticky top-0 z-40 flex w-full justify-center bg-background/60 backdrop-blur-xl transition-all ${
          scroll ? (scrolled ? "border-b" : "bg-transparent") : "border-b"
        }`,
      )}
    >
      <MaxWidthWrapper
        className="flex h-14 items-center justify-between py-4"
        large={documentation}
      >
        <div className="flex gap-6 md:gap-10">
          <Link href="/" className="flex items-center space-x-1.5">
            {/* <span className="font-logo text-xl font-black">WATCHERO</span> */}
            {mounted ? (
              <Image
                src={resolvedTheme === "dark" ? LogoWhite : LogoBlack}
                alt="Watchero Logo"
                className="h-6 w-auto"
              />
            ) : (
              <Image
                src={LogoBlack}
                alt="Watchero Logo"
                className="h-6 w-auto"
              />
            )}
          </Link>

          {links && links.length > 0 ? (
            <nav className="hidden gap-6 md:flex">
              {links.map((item) => {
                const childActive = item.items?.some((child) =>
                  pathname?.startsWith(child.href),
                );
                const isActive =
                  childActive ||
                  (!!item.href && pathname?.startsWith(item.href));

                if (item.items && item.items.length > 0) {
                  return (
                    <DropdownMenu key={item.title}>
                      <DropdownMenuTrigger asChild>
                        <button
                          type="button"
                          className={cn(
                            "flex items-center gap-1 text-lg font-medium transition-colors hover:text-foreground focus:border-0 focus:outline-none focus:ring-0 focus-visible:border-0 focus-visible:outline-none focus-visible:ring-0 sm:text-sm",
                            isActive ? "text-foreground" : "text-foreground/60",
                          )}
                        >
                          {item.title}
                          <ChevronDown
                            className="size-3.5"
                            aria-hidden="true"
                          />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="min-w-52">
                        {item.items.map((child) => (
                          <DropdownMenuItem key={child.href} asChild>
                            <Link
                              href={child.disabled ? "#" : child.href}
                              prefetch={!child.disabled}
                              className={cn(
                                "flex items-center gap-2 text-sm",
                                child.disabled &&
                                  "cursor-not-allowed opacity-80",
                                pathname?.startsWith(child.href) &&
                                  "font-semibold text-foreground",
                              )}
                            >
                              {child.title}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                }

                return (
                  <Link
                    key={item.href}
                    href={item.disabled ? "#" : item.href}
                    prefetch={!item.disabled}
                    className={cn(
                      "flex items-center text-lg font-medium transition-colors hover:text-foreground sm:text-sm",
                      isActive ? "text-foreground" : "text-foreground/60",
                      item.disabled && "cursor-not-allowed opacity-80",
                    )}
                  >
                    {item.title}
                  </Link>
                );
              })}
            </nav>
          ) : null}
        </div>

        <div className="flex items-center space-x-3">
          {/* right header for docs */}
          {documentation ? (
            <div className="hidden flex-1 items-center space-x-4 sm:justify-end lg:flex">
              <div className="hidden lg:flex lg:grow-0">
                <DocsSearch />
              </div>
              <div className="flex lg:hidden">
                <Icons.search className="size-6 text-muted-foreground" />
              </div>
              <div className="flex space-x-4">
                <Link
                  href={siteConfig.links.github}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icons.gitHub className="size-7" />
                  <span className="sr-only">GitHub</span>
                </Link>
              </div>
            </div>
          ) : null}

          {session ? (
            <Link
              href={session.user.role === "ADMIN" ? "/admin" : "/dashboard"}
              className="hidden md:block"
            >
              <Button
                className="gap-2 px-5"
                variant="default"
                size="sm"
                rounded="full"
              >
                <span>Dashboard</span>
              </Button>
            </Link>
          ) : status === "unauthenticated" ? (
            <Button
              className="hidden gap-2 px-5 md:flex"
              variant="default"
              size="sm"
              rounded="full"
              onClick={() => setShowSignInModal(true)}
            >
              <span>Sign In</span>
              <Icons.arrowRight className="size-4" />
            </Button>
          ) : (
            <Skeleton className="hidden h-9 w-28 rounded-full lg:flex" />
          )}
          <div className="hidden md:flex">
            <ModeToggle />
          </div>
        </div>
      </MaxWidthWrapper>
    </header>
  );
}
